import classNames from 'classnames'
import { PropsWithChildren, useMemo } from 'react'
import {
  useFormContext,
  Controller,
  ControllerRenderProps,
  FieldValues,
  Control,
} from 'react-hook-form'

import { Accordion } from '@lounge-fe/ui-kit/src/components/Accordion'
import { Checkbox } from '@lounge-fe/ui-kit/src/components/Checkbox/Checkbox'
import { ChoiceChips } from '@lounge-fe/ui-kit/src/components/ChoiceChips'
import { Swatches } from '@lounge-fe/ui-kit/src/components/Swatches'
import { Text } from '@lounge-fe/ui-kit/src/components/Text'
import { RadioGroup } from '@lounge-fe/ui-kit/src/components/RadioGroup'

import { FilterBySectionProps, Filters } from './types'

const SectionAccordion =
  (title: string, initialOpen: boolean | undefined) =>
  ({ children }: PropsWithChildren) => (
    <Accordion.Item
      className={classNames('py-6')}
      initialOpen={initialOpen}
      title={
        <Text variant="title-six" className="text-md">
          {title}
        </Text>
      }
    >
      <div className="mb-6">{children}</div>
    </Accordion.Item>
  )

const SectionStatic = (props: PropsWithChildren) => (
  <div className="mb-6" {...props} />
)

const renderLabel = (filter: Filters, labelId: string) => (
  <Text
    variant="body-sm"
    className={classNames(
      'font-regular mb-2 text-[#888888]',
      filter.titleHidden && 'sr-only',
    )}
    id={labelId}
  >
    {filter.title}
  </Text>
)

const renderBehavior = (
  field: ControllerRenderProps<FieldValues, string>,
  filter: Filters,
  labelId: string,
) => {
  switch (filter.type) {
    case 'checklist':
      return (
        <div
          role="group"
          className={classNames(
            'grid gap-y-3 justify-items-start',
            filter.name === 'size' ? 'grid-cols-2' : 'grid-cols-1',
          )}
          aria-labelledby={labelId}
        >
          {filter.values.map(({ value, label }) => (
            <Checkbox
              key={value}
              value={value}
              label={label}
              checked={field.value.includes(value)}
              onChange={(evt) => {
                field.onChange(
                  evt.currentTarget.checked
                    ? [...field.value, value]
                    : field.value.filter((val: string) => val !== value),
                )
              }}
            />
          ))}
        </div>
      )

    case 'choicechips':
      return (
        <ChoiceChips
          className="grid grid-cols-[repeat(auto-fit,minmax(min(100%/2,max(50px,100%/7)),max-content))] justify-start"
          value={field.value || []}
          onChange={(value: string[]) => field.onChange(value)}
          aria-labelledby={labelId}
        >
          {filter.values.map(({ value, label }) => (
            <ChoiceChips.Item
              className="font-regular"
              size="md"
              key={value}
              value={value}
            >
              {label}
            </ChoiceChips.Item>
          ))}
        </ChoiceChips>
      )

    case 'swatches':
      return (
        <Swatches
          className="grid grid-cols-2 gap-y-3 justify-items-start"
          value={field.value || []}
          onChange={(value: string[]) => field.onChange(value)}
          aria-labelledby={labelId}
        >
          {filter.values.map(({ value, label, color }) => (
            <Swatches.Item key={value} value={value} color={color}>
              {label}
            </Swatches.Item>
          ))}
        </Swatches>
      )

    case 'radiogroup':
      return (
        <RadioGroup
          {...field}
          value={field.value ?? ''}
          onChange={(...args) => field.onChange(...args)}
        >
          {filter.values.map(({ value, label }) => (
            <RadioGroup.Item key={value} value={value} label={label}>
              {label}
            </RadioGroup.Item>
          ))}
        </RadioGroup>
      )

    default:
      return <></>
  }
}

const renderController = (
  filter: Filters,
  labelId: string,
  control: Control<FieldValues>,
) => (
  <Controller
    name={filter.name}
    control={control}
    defaultValue={[]}
    render={({ field }) => renderBehavior(field, filter, labelId)}
  />
)

export const Section = ({
  section: { title, filters },
  initialOpen,
}: FilterBySectionProps) => {
  const MaybeAccordion = useMemo(
    () => (title ? SectionAccordion(title, initialOpen) : SectionStatic),
    [title],
  )

  const { control } = useFormContext()

  return (
    <MaybeAccordion>
      <div className="flex flex-col gap-y-4">
        {filters.map((filter: Filters) => {
          const labelId = `${filter.name}-label`
          return (
            <div key={filter.name}>
              {renderLabel(filter, labelId)}
              {renderController(filter, labelId, control)}
            </div>
          )
        })}
      </div>
    </MaybeAccordion>
  )
}
